import { useEffect, useState } from 'react';
import { Evidence } from '../types/types';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Hook to enhance evidence with tags from Supabase
 */
export function useEvidenceEnhancement(evidence: Evidence[]) {
  const [enhancedEvidence, setEnhancedEvidence] = useState<Evidence[]>(evidence);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    console.log('useEvidenceEnhancement - Supabase configured:', isSupabaseConfigured());
    console.log('useEvidenceEnhancement - Evidence count:', evidence?.length || 0);
    
    if (!isSupabaseConfigured() || !evidence || evidence.length === 0) {
      setEnhancedEvidence(evidence);
      return;
    }

    async function fetchTags() {
      setIsLoading(true);
      try {
        // Get all evidence IDs
        const evidenceIds = evidence.map(e => e.evidence_id).filter(id => id);
        
        if (evidenceIds.length === 0) {
          setEnhancedEvidence(evidence);
          return;
        }

        // Fetch all tags for these evidence items
        console.log('Fetching tags for evidence IDs:', evidenceIds.slice(0, 5), '...');
        const { data: tagsData, error } = await supabase
          .from('evidence_tags')
          .select(`
            evidence_id,
            impact_streams (
              id,
              name,
              module_id,
              modules (
                id,
                name,
                color
              )
            )
          `)
          .in('evidence_id', evidenceIds);

        if (error) {
          console.error('Error fetching tags:', error);
          setEnhancedEvidence(evidence);
          return;
        }
        
        console.log('Tags fetched:', tagsData?.length || 0, 'tags');

        // Group tags by evidence_id
        const tagsByEvidence = new Map();
        (tagsData || []).forEach(tag => {
          if (!tag.impact_streams) return;
          
          const evidenceId = tag.evidence_id;
          if (!tagsByEvidence.has(evidenceId)) {
            tagsByEvidence.set(evidenceId, []);
          }
          
          tagsByEvidence.get(evidenceId).push({
            stream_id: tag.impact_streams.id,
            stream_name: tag.impact_streams.name,
            module_id: tag.impact_streams.module_id,
            module_name: tag.impact_streams.modules?.name || '',
            module_color: tag.impact_streams.modules?.color || '#gray'
          });
        });

        // Enhance evidence with tags
        const enhanced = evidence.map(item => ({
          ...item,
          tags: tagsByEvidence.get(item.evidence_id) || []
        }));

        setEnhancedEvidence(enhanced);
      } catch (err) {
        console.error('Error enhancing evidence with tags:', err);
        setEnhancedEvidence(evidence);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTags();
  }, [evidence]);

  return { enhancedEvidence, isLoading };
}